import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import ShellSaac, { useVoltar } from '../../../components/shell/ShellSaac';
import {
  Botao,
  Chip,
  ColunaDataList,
  DataList,
  EmptyState,
  ErrorState,
  PageHeader,
  Pagination,
  SearchInput,
  Skeleton,
  Tabs,
  idDaAba,
  idDoPainel,
} from '../../../ui';

import RascunhosAdesaoTab from './RascunhosAdesaoTab';
import ModalDetalhesAdesao from '../../../components/modal/detalhes-adesao/ModalDetalhesAdesao';
import AvisoDivergenciaSga from '../../../components/divergencia-sga/AvisoDivergenciaSga';

import { iniciarNovaAdesao } from '../../../utils/adesaoFluxo';
import { listarRascunhos } from '../../../api/rascunho-adesao/rascunhoAdesaoApi';
import { fetchAssociadosPorVolunteerCode, Associado } from '../../../api/associado/associadoApi';
import { buscarProdutosDoCarro } from '../../../api/sga-produto/sga-produtoApi';
import {
  reconciliarAssociado,
  trazerAssociadoDoSga,
} from '../../../api/sga-reconciliacao/sgaReconciliacaoApi';
import { DivergenciaSga } from '../../../api/sga-divergencia/sgaDivergenciaApi';
import { interpretarVerificacaoSga } from '../../../utils/verificacaoSga';
import { extrairMensagemErro } from '../../../utils/apiError';
import { formatCpfCnpj } from '../../../utils/formartCPF';
import { isCarroVivo } from '../../../utils/carroSituacao';
import { decodeJwtPayload } from '../../../utils/jwt';

import { ASSOCIADO } from '../../associado/routes';
import { ADESAOSCONSULTAR } from '../routes';

/**
 * `/adesoes` — acompanhamento do consultor sobre os próprios cadastros
 * (reescrita na at-56, frente B do refactor de front).
 *
 * ⚠️ **Não é onde a adesão é feita** (spec 02 §1): o fluxo de cadastro começa em
 * `/associado`. Aqui o consultor vê o estado do que já cadastrou, confere contra
 * o SGA e retoma o que ficou pela metade.
 *
 * ## O que a reescrita mudou de COMPORTAMENTO (spec 02 §2.7 e medições da Fase 1)
 *
 *   1. O carregamento **não esconde mais as abas nem a busca** — era um
 *      `FullScreenLoader`, que é um overlay de tela cheia; virou `Skeleton` no
 *      lugar da lista.
 *   2. **Erro da listagem aparece**, com "Tentar novamente" — antes caía num
 *      `console.error` e a lista ficava vazia em silêncio, indistinguível de
 *      "você não tem cadastros".
 *   3. **Vazio-por-busca ≠ vazio-sem-cadastros** — eram o mesmo texto.
 *   4. O indicador de produtos tem estado **"Calculando"** e as chamadas foram
 *      para **paralelo** — ver `useEffect` do N+1, abaixo.
 *   5. **CPF formatado** na lista (só o modal formatava).
 *   6. Paginação nunca mais mostra **"Página 1 de 0"** (regra dentro do
 *      `Pagination`).
 *   7. Saíram ~60 linhas de estado de modais **que não eram renderizados** (spec
 *      §2.8: "são resquícios; nenhuma dessas ações faz parte da tela").
 *
 * ## O que NÃO mudou, de propósito (contratos da spec 02 §8)
 *
 * "Nova Adesão +" limpa o estado do fluxo; a reconciliação é só por clique e
 * nunca vira erro na tela; a faixa de divergências não bloqueia nada e não
 * oferece "Enviar ao SGA" (a API do SGA não tem alteração cadastral de
 * associado); o deep-link de `/associado` abre direto na aba de rascunhos; e o
 * contador "Em andamento (N)" existe antes de a aba ser aberta.
 */

type AbaAdesoes = 'adesoes' | 'rascunhos';

/**
 * Estado da consulta de produtos de **um carro**.
 *
 * ⚠️ O mapa é indexado por `carroId`, e não por associado, porque é essa a chave
 * que o `ModalDetalhesAdesao` (at-54) consome. Indexar por associado obrigaria a
 * mudar a interface do modal — que também é o desenho de referência do
 * `ModalDetalhesCarteira`.
 */
type EstadoProdutosCarro = 'calculando' | 'vinculado' | 'nao_vinculado';

/** O que o chip da linha mostra — acrescenta o caso "o associado não tem carro". */
type EstadoProdutos = EstadoProdutosCarro | 'sem_veiculo';

type EstadoLista = 'carregando' | 'ok' | 'erro';

const LIMITE = 10;
const ID_BASE = 'adesoes';

const Adesoes: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const voltar = useVoltar();

  const volunteerCode = useSelector((state: any) => state.auth.codigoAssociado);
  const accessToken = useSelector((state: any) => state.auth.accessToken);

  // Primeiro nome ao lado do título, como no protótipo. Vem do JWT — o
  // `authSlice` não guarda o nome (mesma leitura que a Home faz).
  const primeiroNome = useMemo(() => {
    if (!accessToken) return null;
    const payload = decodeJwtPayload(accessToken) as { nome?: string } | null;
    return payload?.nome?.trim().split(/\s+/)[0] ?? null;
  }, [accessToken]);

  /**
   * Aba ativa. A faixa de `/associado` navega para cá com
   * `state: { aba: 'rascunhos' }` — sem isso o consultor cairia na aba de
   * adesões e teria de descobrir a outra sozinho (spec §1 e §8.7).
   */
  const [aba, setAba] = useState<AbaAdesoes>(
    (location.state as { aba?: AbaAdesoes } | null)?.aba === 'rascunhos' ? 'rascunhos' : 'adesoes',
  );
  const [totalRascunhos, setTotalRascunhos] = useState<number | null>(null);

  // ── aba A ─────────────────────────────────────────────────────────────────
  const [associados, setAssociados] = useState<Associado[]>([]);
  const [page, setPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [estadoLista, setEstadoLista] = useState<EstadoLista>('carregando');
  const [erroLista, setErroLista] = useState('');
  const [busca, setBusca] = useState('');
  const [produtos, setProdutos] = useState<Record<string, EstadoProdutosCarro>>({});

  const [associadoDetalhe, setAssociadoDetalhe] = useState<Associado | null>(null);
  const [verificandoSgaId, setVerificandoSgaId] = useState<string | null>(null);
  const [divergenciasSga, setDivergenciasSga] = useState<DivergenciaSga[]>([]);
  const [associadoReconciliadoId, setAssociadoReconciliadoId] = useState('');
  const [sincronizandoSga, setSincronizandoSga] = useState(false);

  /**
   * Contador da aba, buscado uma vez no mount: sem isto o "Em andamento (N)" só
   * ganharia o número depois de o consultor abrir a aba — e é justamente o
   * número que faz ele querer abri-la (spec §3.3). Falha é silenciosa: o rótulo
   * fica sem contador e nada nesta tela depende do rascunho.
   */
  useEffect(() => {
    let vivo = true;
    listarRascunhos({ limit: 1 })
      .then((r) => {
        if (vivo) setTotalRascunhos(r.totais?.emAndamento ?? 0);
      })
      .catch(() => {
        /* contador é acessório */
      });
    return () => {
      vivo = false;
    };
  }, []);

  const buscarAssociados = useCallback(async () => {
    setEstadoLista('carregando');
    try {
      const data = await fetchAssociadosPorVolunteerCode(volunteerCode, page, LIMITE, busca);
      setAssociados(data.items);
      setTotalItems(data.totalItems);
      setEstadoLista('ok');
    } catch (erro) {
      // A falha era MUDA (console.error + lista vazia). Agora a tela diz.
      setAssociados([]);
      setTotalItems(0);
      setErroLista(extrairMensagemErro(erro, 'Não foi possível carregar seus cadastros.'));
      setEstadoLista('erro');
    }
  }, [volunteerCode, page, busca]);

  /**
   * A lista só é buscada quando a aba dela está visível — com a aba de rascunhos
   * aberta, esta chamada seria trabalho jogado fora.
   */
  useEffect(() => {
    if (volunteerCode && aba === 'adesoes') void buscarAssociados();
  }, [volunteerCode, aba, buscarAssociados]);

  /**
   * Indicador de produtos — o N+1 do contrato do backend (spec §2.4).
   *
   * ⚠️ Três defeitos do código anterior foram corrigidos aqui:
   *
   *   • era um `for … await` **sequencial**;
   *   • rodava para **todos** os carros da página, mas a tabela lê só o primeiro;
   *   • gravava o estado **uma vez no fim**, então durante toda a espera o mapa
   *     estava vazio e a bolinha saía **VERMELHA** — a spec §2.4 proíbe isso em
   *     tantas palavras ("não um vermelho provisório que depois vira verde"), e o
   *     efeito era a tela acusar pendência que ela ainda não sabia se existia.
   *
   * ⚠️ **Nada aqui depende da ordem das chamadas** (exigência da §2.4): se o
   * backend passar a devolver a flag na listagem, este efeito simplesmente sai.
   */
  useEffect(() => {
    if (!associados.length) {
      setProdutos({});
      return undefined;
    }

    // Primeiro carro EM CIRCULAÇÃO de cada associado — é só ele que a coluna
    // reflete (§2.4). O backend já filtra substituídos (§2.1), mas o
    // `ModalDetalhesAdesao` também refiltra: os dois concordarem importa mais que
    // economizar um filtro que é no-op.
    const carrosAlvo = associados
      .map((associado) => (associado.carros ?? []).filter(isCarroVivo)[0]?.id)
      .filter((carroId): carroId is string => !!carroId);

    setProdutos(Object.fromEntries(carrosAlvo.map((carroId) => [carroId, 'calculando'])));

    let vivo = true;
    void Promise.all(
      carrosAlvo.map(async (carroId) => {
        let resultado: EstadoProdutosCarro = 'nao_vinculado';
        try {
          const lista = await buscarProdutosDoCarro(carroId);
          resultado = lista.some((produto) => produto.vinculadoAoSga)
            ? 'vinculado'
            : 'nao_vinculado';
        } catch {
          // Falha conta como não vinculado — comportamento preservado (§2.4).
        }
        if (vivo) setProdutos((anterior) => ({ ...anterior, [carroId]: resultado }));
      }),
    );

    return () => {
      vivo = false;
    };
  }, [associados]);

  const trocarBusca = (termo: string) => {
    setBusca(termo);
    setPage(1);
  };

  /** Estado do chip da linha, derivado do primeiro carro em circulação. */
  const estadoProdutosDo = (associado: Associado): EstadoProdutos => {
    const carroId = (associado.carros ?? []).filter(isCarroVivo)[0]?.id;
    if (!carroId) return 'sem_veiculo';
    return produtos[carroId] ?? 'calculando';
  };

  /**
   * O que o `ModalDetalhesAdesao` espera: `Record<carroId, boolean>`.
   * ⚠️ `calculando` vira `false`, e é aceitável porque o modal só abre por clique
   * — sempre depois de a lista ter carregado.
   */
  const produtosVinculadosPorCarro = Object.fromEntries(
    Object.entries(produtos).map(([carroId, estado]) => [carroId, estado === 'vinculado']),
  );

  /**
   * Reconciliação on-access (at-32): SOB DEMANDA, uma chamada por clique. Não é
   * disparada no carregamento da lista de propósito — seriam 10+ idas ao SGA por
   * página (spec §2.5).
   */
  const verificarNoSga = async (associado: Associado) => {
    if (!associado.id) return;

    setVerificandoSgaId(associado.id);
    setAssociadoReconciliadoId(associado.id);
    setDivergenciasSga([]);
    try {
      // Nunca lança: SGA fora do ar volta com verificado=false e a tela segue.
      const resultado = await reconciliarAssociado(associado.id);

      // at-76: a regra de leitura do desfecho virou `utils/verificacaoSga.ts`,
      // compartilhada com `/ti/envios` (convenção at-24). A reexibição do que foi
      // dispensado acontece lá dentro — at-54, "quem pede para ver, vê".
      const leitura = interpretarVerificacaoSga(resultado);

      if (leitura.situacao === 'indisponivel') {
        toast.info(leitura.mensagem);
      } else if (leitura.situacao === 'sem-diferencas') {
        toast.success(leitura.mensagem);
      } else if (leitura.situacao === 'veiculo-vinculado') {
        // at-78: achado no SGA (por placa ou pelo chassi) e vinculado — o consultor
        // precisa saber, porque é o que tira o veículo da fila de pendentes.
        toast.success(leitura.mensagem, { autoClose: 8000 });
        setDivergenciasSga(leitura.divergencias);
        await buscarAssociados();
      } else if (leitura.situacao === 'veiculo-pendente') {
        // at-78 (B23): associado conferido, veículo NÃO encontrado no SGA. Antes
        // caía em "nenhuma diferença", e o consultor concluía que estava resolvido.
        toast.info(leitura.mensagem, { autoClose: 8000 });
      } else {
        // Sem toast quando nada foi reexibido: quem conta a história aqui é a faixa
        // `AvisoDivergenciaSga`, logo abaixo na tela. O toast só entra no caso que
        // a faixa não explicaria sozinha — algo dispensado voltando a aparecer.
        if (leitura.reexibidas > 0) {
          toast.info(leitura.mensagem, { autoClose: 6000 });
        }
        setDivergenciasSga(leitura.divergencias);
      }
    } finally {
      setVerificandoSgaId(null);
    }
  };

  /**
   * SGA → local para o associado (at-32). Só este sentido: a API do SGA não
   * expõe alteração cadastral de associado (inventário de 31/08/2026), então a
   * faixa não oferece "enviar ao SGA" aqui (§2.6/§8.4).
   */
  const trazerAssociadoDoSgaHandler = async () => {
    if (!associadoReconciliadoId) return;

    setSincronizandoSga(true);
    try {
      const r = await trazerAssociadoDoSga(associadoReconciliadoId);
      toast.success(
        r.aplicados.length
          ? `${r.aplicados.length} campo(s) atualizado(s) com os dados do SGA.`
          : 'Nenhum campo pôde ser trazido automaticamente.',
      );
      if (r.pendentes.length) {
        toast.info(`${r.pendentes.length} diferença(s) exigem a equipe de TI.`);
      }

      // Aqui a escrita foi no banco LOCAL, sem defasagem de leitura — por isso
      // este caminho reconcilia de novo (ao contrário do "enviar ao SGA", at-47).
      const atualizado = await reconciliarAssociado(associadoReconciliadoId);
      setDivergenciasSga(atualizado.divergencias);
      await buscarAssociados();
    } catch (erro) {
      toast.error(extrairMensagemErro(erro, 'Não foi possível sincronizar com o SGA.'));
    } finally {
      setSincronizandoSga(false);
    }
  };

  /**
   * "Nova Adesão +" = começar uma adesão NOVA. Sem a limpeza, o formulário
   * nascia com o que sobrou do fluxo anterior (merge raso no submit) e, pior,
   * herdava o `rascunhoId` da adesão passada (at-38; contrato §8.2).
   */
  const novaAdesao = () => {
    iniciarNovaAdesao(dispatch);
    navigate(ASSOCIADO());
  };

  const ABAS: Array<{ id: AbaAdesoes; rotulo: string }> = [
    { id: 'adesoes', rotulo: 'Adesões' },
    {
      id: 'rascunhos',
      rotulo: totalRascunhos === null ? 'Em andamento' : `Em andamento (${totalRascunhos})`,
    },
  ];

  // ── colunas da aba A ──────────────────────────────────────────────────────
  // Ordem decidida por Victor na at-54: o NOME primeiro e como alvo de clique.
  // Antes as três primeiras colunas eram bolinhas coloridas sem texto, e no
  // celular a tabela virava scroller horizontal — o nome, que é a única coisa
  // pela qual o consultor procura, ficava fora da tela.
  const colunas: ColunaDataList<Associado>[] = [
    {
      id: 'nome',
      rotulo: 'Nome Completo',
      principal: true,
      celula: (associado) => (
        <button
          type="button"
          onClick={() => setAssociadoDetalhe(associado)}
          title="Ver detalhes desta adesão"
          className="tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-0 tw-text-left tw-font-marca tw-text-sm tw-font-bold tw-text-brand tw-underline tw-underline-offset-2 hover:tw-text-brand-deep focus-visible:tw-rounded focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-brand/50 [overflow-wrap:anywhere]"
        >
          {associado.nomeCompleto}
        </button>
      ),
    },
    {
      id: 'cpf',
      rotulo: 'CPF/CNPJ',
      secundaria: true,
      // Era exibido cru, só dígitos (§2.3 pede formatado).
      celula: (associado) => (
        <span className="tw-tabular-nums">{formatCpfCnpj(String(associado.cpfCnpj || ''))}</span>
      ),
    },
    {
      id: 'associado-sga',
      rotulo: 'Associado no SGA',
      celula: (associado) =>
        associado.addedToSga === true ? (
          <Chip variante="ok">Enviado</Chip>
        ) : (
          <Chip variante="pendente">Pendente</Chip>
        ),
    },
    {
      id: 'veiculo-sga',
      rotulo: 'Veículo no SGA',
      celula: (associado) => {
        const vivos = (associado.carros ?? []).filter(isCarroVivo);
        if (!vivos.length) return <Chip variante="neutro">Sem veículo</Chip>;
        return vivos.some((carro) => carro.addedToSga) ? (
          <Chip variante="ok">Enviado</Chip>
        ) : (
          <Chip variante="pendente">Pendente</Chip>
        );
      },
    },
    {
      id: 'produtos-sga',
      rotulo: 'Produtos no SGA',
      celula: (associado) => <ChipProdutos estado={estadoProdutosDo(associado)} />,
    },
  ];

  /*
   * `tamanho="sm"` é a medida do protótipo para ação de linha (`px-3 py-1.5
   * text-xs`) — com `md` o botão ficava alto e desproporcional numa coluna onde
   * se repete a cada linha, empurrando a altura da tabela.
   *
   * ⚠️ Custo assumido: 32px de altura fica abaixo dos 44px que a spec 02 §9 pede
   * para as ações de linha. No **card do celular** isso é compensado pela largura
   * total (`w-full`), que é onde o dedo erra o alvo; no desktop, o ponteiro não
   * tem esse problema. Se for preciso 44px também no celular, o caminho é dar ao
   * `Botao` um tamanho responsivo — não sobrescrever a altura por `className`,
   * que cai no empate de utilitários descrito no `Card.tsx`.
   */
  const acoesDaLinha = (associado: Associado) => (
    <Botao
      variante="secundario"
      tamanho="sm"
      disabled={verificandoSgaId === associado.id}
      onClick={() => void verificarNoSga(associado)}
      className="tw-w-full md:tw-w-auto"
    >
      {verificandoSgaId === associado.id ? 'Conferindo...' : 'Verificar no SGA'}
    </Botao>
  );

  const conteudoAbaA = () => {
    if (estadoLista === 'carregando') return <Skeleton linhas={4} />;

    if (estadoLista === 'erro') {
      return (
        <ErrorState
          mensagem="Não foi possível carregar a lista de adesões."
          detalhe={erroLista}
          aoTentarNovamente={() => void buscarAssociados()}
        />
      );
    }

    if (!associados.length) {
      // Vazio-por-busca e vazio-sem-cadastros pedem ações opostas do usuário
      // (§2.7) — antes eram o mesmo texto.
      return busca ? (
        <EmptyState
          titulo="Nenhum resultado para essa busca"
          descricao="A busca casa nome, CPF, placa e modelo — e cobre apenas a sua carteira."
          icone="busca"
          /*
           * A porta de entrada da consulta por documento vive AQUI, e não numa
           * faixa fixa no pé da lista (que Victor mandou sumir em 09/09/2026, e
           * com razão: era ruído permanente para um caso raro).
           *
           * ⚠️ Mas ela precisa existir em algum lugar: até a at-56 **nenhuma tela
           * levava a `/adesoes/consultar`** — só a URL digitada à mão —, e é a
           * única tela do consultor que mostra veículo substituído/excluído
           * (spec 02 §5). Este é o momento exato em que ela é útil: o consultor
           * procurou alguém e não achou na própria carteira.
           */
          acao={
            <Botao
              variante="secundario"
              tamanho="md"
              onClick={() => navigate(ADESAOSCONSULTAR())}
            >
              Consultar por CPF/CNPJ
            </Botao>
          }
        />
      ) : (
        <EmptyState
          titulo="Você ainda não tem cadastros"
          descricao="As adesões que você cadastrar aparecem aqui."
          acao={
            <Botao variante="primario" tamanho="md" onClick={novaAdesao}>
              Nova Adesão +
            </Botao>
          }
        />
      );
    }

    return (
      <DataList
        itens={associados}
        colunas={colunas}
        chave={(associado) => associado.id}
        acoes={acoesDaLinha}
        rotuloAcoes="Conferir no SGA"
        rotuloAcessivel="Adesões cadastradas por você"
      />
    );
  };

  return (
    <ShellSaac
      cabecalho={
        <PageHeader
          titulo="Adesões"
          complemento={primeiroNome ?? undefined}
          aoVoltar={voltar}
          acaoDireita={
            /* ⚠️ `md` (44px) e não `sm`: é a ação principal da tela e a spec 01 §7
               exige esse alvo de toque. Custo aceito: no mobile estreito o botão
               disputa a linha com o título, que trunca (o título é curto).
               📌 O "+" é ÍCONE, como no protótipo — era um "+" no fim do texto. */
            <Botao variante="primario" tamanho="md" iconeEsquerda="mais" onClick={novaAdesao}>
              Nova Adesão
            </Botao>
          }
        />
      }
    >
      {/*
        Estrutura do protótipo (`inspo/.../pages/Adesoes.tsx`), com UMA árvore
        para as duas larguras — ele duplica tudo entre `lg:hidden` e
        `hidden lg:flex`, e importar essa duplicação era o erro a evitar (achado
        A6 do plano 0):

          • **desktop** — card branco com `m-6`, raio, borda e sombra difusa;
            abas com `px-2` (o respiro entre a primeira aba e a borda do card);
            painel `p-6` branco.
          • **mobile** — sem card: a faixa de abas vai de borda a borda e o
            conteúdo fica sobre o cinza, o que dá contraste aos cards da lista.
      */}
      <div className="tw-min-h-full tw-bg-shell">
        {/* ⚠️ O breakpoint desta tela é `md` (768px), o mesmo em que o `DataList`
            troca cards por tabela. O protótipo usa `lg` porque o layout dele muda
            de sidebar ali; aqui isso deixaria uma faixa incoerente entre 768 e
            1024 — tabela sobre painel ainda cinza e sem card. */}
        <div className="tw-mx-auto tw-w-full tw-max-w-6xl md:tw-p-6">
          <div className="tw-overflow-hidden tw-border-0 tw-bg-white md:tw-rounded-2xl md:tw-border md:tw-border-superficie-borda md:tw-shadow-[0_2px_12px_rgba(0,0,0,0.09)]">
            {/* `px-2`: sem isto a primeira aba cola na borda do card. */}
            <div className="tw-shrink-0 md:tw-px-2">
              <Tabs
                idBase={ID_BASE}
                abas={ABAS}
                ativa={aba}
                aoTrocar={setAba}
                rotuloAcessivel="Seções das adesões"
              />
            </div>

            {/* No celular o painel é neutro para os cards brancos contrastarem;
                no desktop é branco, e é a tabela que tem a borda. */}
            <div
              id={idDoPainel(ID_BASE)}
              role="tabpanel"
              aria-labelledby={idDaAba(ID_BASE, aba)}
              tabIndex={-1}
              aria-busy={aba === 'adesoes' && estadoLista === 'carregando'}
              className="tw-bg-shell tw-p-4 md:tw-bg-white md:tw-p-6"
            >
              {aba === 'rascunhos' ? (
                <RascunhosAdesaoTab onTotalEmAndamento={setTotalRascunhos} />
              ) : (
                <div className="tw-flex tw-flex-col tw-gap-4">
                  {/* Largura TOTAL, alinhada com a tabela abaixo. Com
                      `max-w-md` o campo ficava com metade da largura do painel e
                      a borda parecia solta no meio do card. */}
                  <SearchInput
                    aoBuscar={trocarBusca}
                    placeholder="Buscar por nome, CPF, placa ou modelo"
                    rotuloAcessivel="Buscar adesões por nome, CPF, placa ou modelo"
                  />

                  {/* Nunca bloqueia o fluxo: é informação, não trava (§2.6). */}
                  <AvisoDivergenciaSga
                    divergencias={divergenciasSga}
                    processando={sincronizandoSga}
                    onTrazerDoSga={trazerAssociadoDoSgaHandler}
                  />

                  {conteudoAbaA()}

                  {estadoLista === 'ok' && associados.length > 0 && (
                    <Pagination
                      pagina={page}
                      totalItens={totalItems}
                      porPagina={LIMITE}
                      aoTrocar={setPage}
                    />
                  )}

                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <ModalDetalhesAdesao
        associado={associadoDetalhe}
        produtosVinculados={produtosVinculadosPorCarro}
        verificando={verificandoSgaId === associadoDetalhe?.id}
        onFechar={() => setAssociadoDetalhe(null)}
        onVerificarNoSga={() => {
          if (associadoDetalhe) void verificarNoSga(associadoDetalhe);
        }}
      />
    </ShellSaac>
  );
};

/**
 * Chip do indicador de produtos.
 *
 * ⚠️ O `title` não é enfeite: o contrato do backend considera **apenas o primeiro
 * veículo** (§2.4), e o associado com dois carros vê um indicador que fala por um
 * só. Isso não estava dito em lugar nenhum da tela. Quem quiser veículo por
 * veículo abre o `ModalDetalhesAdesao`, que os lista.
 */
const ChipProdutos: React.FC<{ estado: EstadoProdutos }> = ({ estado }) => {
  if (estado === 'calculando') {
    // Nem "enviado" nem "pendente": ainda não sabemos. Era aqui que a tela
    // mentia, mostrando vermelho antes de a chamada responder.
    return <Chip variante="neutro">Calculando</Chip>;
  }
  if (estado === 'sem_veiculo') return <Chip variante="neutro">Sem veículo</Chip>;

  return estado === 'vinculado' ? (
    <Chip variante="ok" title="Reflete o primeiro veículo do associado">
      Vinculados
    </Chip>
  ) : (
    <Chip variante="pendente" title="Reflete o primeiro veículo do associado">
      Pendente
    </Chip>
  );
};

export default Adesoes;
